/**
 * CameraManager — R3F component wrapping Drei's CameraControls.
 *
 * Features:
 * - Configurable orbit constraints (polar, azimuth, distance)
 * - Auto-rotation with idle timeout (resumes after no interaction)
 * - Smooth damping
 * - Imperative ref for programmatic camera moves
 * - Runtime config updates (setCamera, setViewpoint, presets)
 *
 * Uses `camera-controls` via `@react-three/drei` — NOT OrbitControls.
 * @see docs/ims-engine-api/08-feature-camera-views.md
 *
 * @future Elliptical camera orbit — allow non-circular orbit paths for
 * dynamic, cinematic camera movement. Inspired by PlayCanvas implementations.
 * The elliptical path would modulate minDistance/maxDistance based on
 * azimuth angle, creating a natural oval orbit around the vehicle.
 * See docs/ims-engine-api/08-feature-camera-views.md for details.
 */

import { useRef, useEffect, useCallback, useImperativeHandle, useMemo, forwardRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { CameraControls } from '@react-three/drei';
import type CameraControlsImpl from 'camera-controls';

import type { CameraConfig, AutoRotateConfig, CameraPreset, CameraViewpoint } from '../api/types';
import { devWarn } from '../utils/devLog';
import {
  DEFAULT_CAMERA,
  DEFAULT_AUTO_ROTATE,
  AUTOROTATE_SPEED_PRESETS,
  CAMERA_PRESETS,
} from './CameraManager.constants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CameraManagerProps {
  /** Camera orbit configuration. */
  config?: CameraConfig;
  /** Auto-rotation configuration. */
  autoRotate?: AutoRotateConfig;
  /** Callback when auto-rotation state changes. */
  onAutoRotateChange?: (rotating: boolean) => void;
}

export interface CameraManagerRef {
  /** The underlying camera-controls instance. */
  controls: CameraControlsImpl | null;
  /** Reset camera to default position. */
  reset: () => void;
  /** Update camera orbit config at runtime (partial merge). */
  setCamera: (config: Partial<CameraConfig>) => void;
  /** Transition camera to a viewpoint. */
  setViewpoint: (viewpoint: CameraViewpoint) => void;
  /** Apply a camera behaviour preset. */
  setCameraPreset: (preset: CameraPreset) => void;
  /** Update auto-rotation config at runtime (partial merge). */
  setAutoRotate: (config: Partial<AutoRotateConfig>) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CameraManager = forwardRef<CameraManagerRef, CameraManagerProps>(
  function CameraManager({ config, autoRotate, onAutoRotateChange }, ref) {
    const controlsRef = useRef<CameraControlsImpl>(null);
    const autoRotateOverrideRef = useRef<Partial<AutoRotateConfig> | null>(null);

    /**
     * Stores the base camera constraints that were active before any
     * per-viewpoint overrides were applied. Used to restore constraints
     * when leaving a viewpoint (restoreOnLeave: true).
     */
    const baseCameraConfigRef = useRef<Partial<CameraConfig> | null>(null);
    /** Whether orbit interaction has been disabled by a viewpoint override. */
    const orbitDisabledRef = useRef(false);

    /**
     * The "active" base FOV — used by the landscape hFOV clamp instead of
     * the static `cam.fov`. Updated whenever `setCameraPreset` or `setCamera`
     * changes the FOV at runtime (e.g. pano mode sets 70°). This ensures the
     * landscape clamp computes from the correct value, not the initial prop.
     */
    const activeFovRef = useRef(config?.fov ?? DEFAULT_CAMERA.fov);

    /**
     * Active max horizontal FOV — updated by presets (e.g. pano sets Infinity
     * to disable the landscape clamp for wide panorama viewing).
     */
    const activeMaxHFovRef = useRef(config?.maxHFov ?? DEFAULT_CAMERA.maxHFov);

    /**
     * Single source of truth for auto-rotate pause.
     * - `0`        → not paused (auto-rotate runs if enabled)
     * - `Infinity`  → paused indefinitely (during drag or viewpoint transition)
     * - `timestamp` → paused until that time, then auto-rotate resumes
     */
    const pausedUntilRef = useRef<number>(0);
    const wasRotatingRef = useRef(false);
    /** Timestamp when ease-in started (0 = not easing). */
    const easeStartRef = useRef<number>(0);

    /** Cached merged auto-rotate config — avoids per-frame spread allocation. */
    const rotConfigRef = useRef({
      ...DEFAULT_AUTO_ROTATE,
      ...autoRotate,
    });

    // Update cached config when the autoRotate prop changes.
    useEffect(() => {
      rotConfigRef.current = {
        ...DEFAULT_AUTO_ROTATE,
        ...autoRotate,
        ...autoRotateOverrideRef.current,
      };
    }, [autoRotate]);

    /** Whether a viewpoint transition is in progress (needs continuous invalidation). */
    const transitionActiveRef = useRef(false);

    /** Read the current merged auto-rotate config (allocation-free). */
    const getRotConfig = useCallback(() => rotConfigRef.current, []);

    // Merge with defaults (memoized to avoid re-triggering effects)
    const cam = useMemo(
      () => ({ ...DEFAULT_CAMERA, ...config }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [
        config?.fov,
        config?.minPolarAngle,
        config?.maxPolarAngle,
        config?.minAzimuthAngle,
        config?.maxAzimuthAngle,
        config?.minDistance,
        config?.maxDistance,
        config?.smoothTime,
        config?.draggingSmoothTime,
        config?.truckSpeed,
        config?.target?.x,
        config?.target?.y,
        config?.target?.z,
      ],
    );

    // --- Apply config to controls instance ---
    const applyConfig = useCallback((cfg: Partial<CameraConfig>) => {
      const controls = controlsRef.current;
      if (!controls) return;

      if (cfg.minPolarAngle !== undefined) controls.minPolarAngle = cfg.minPolarAngle;
      if (cfg.maxPolarAngle !== undefined) controls.maxPolarAngle = cfg.maxPolarAngle;
      if (cfg.minAzimuthAngle !== undefined) controls.minAzimuthAngle = cfg.minAzimuthAngle;
      if (cfg.maxAzimuthAngle !== undefined) controls.maxAzimuthAngle = cfg.maxAzimuthAngle;
      if (cfg.minDistance !== undefined) controls.minDistance = cfg.minDistance;
      if (cfg.maxDistance !== undefined) controls.maxDistance = cfg.maxDistance;
      if (cfg.smoothTime !== undefined) controls.smoothTime = cfg.smoothTime;
      if (cfg.draggingSmoothTime !== undefined)
        controls.draggingSmoothTime = cfg.draggingSmoothTime;
      if (cfg.truckSpeed !== undefined) controls.truckSpeed = cfg.truckSpeed;
      if (cfg.target) {
        controls.setTarget(cfg.target.x, cfg.target.y, cfg.target.z, true);
      }
      if (cfg.fov !== undefined) {
        // camera-controls wraps a PerspectiveCamera — update FOV directly.
        const cam = (
          controls as unknown as {
            camera: {
              isPerspectiveCamera?: boolean;
              fov: number;
              updateProjectionMatrix: () => void;
            };
          }
        ).camera;
        if (cam?.isPerspectiveCamera) {
          cam.fov = cfg.fov;
          cam.updateProjectionMatrix();
        }
        // Keep the landscape hFOV clamp in sync with the new base FOV.
        activeFovRef.current = cfg.fov;
      }
      if (cfg.maxHFov !== undefined) {
        activeMaxHFovRef.current = cfg.maxHFov;
      }
    }, []);

    /**
     * Snapshot the current orbit constraints from the controls instance
     * so they can be restored later.
     */
    const snapshotBaseConfig = useCallback((): Partial<CameraConfig> | null => {
      const c = controlsRef.current;
      if (!c) return null;
      return {
        minPolarAngle: c.minPolarAngle,
        maxPolarAngle: c.maxPolarAngle,
        minAzimuthAngle: c.minAzimuthAngle,
        maxAzimuthAngle: c.maxAzimuthAngle,
        minDistance: c.minDistance,
        maxDistance: c.maxDistance,
      };
    }, []);

    /**
     * Restore base camera constraints (if any were saved) and re-enable orbit.
     */
    const restoreBaseConfig = useCallback(() => {
      if (baseCameraConfigRef.current) {
        applyConfig(baseCameraConfigRef.current);
        baseCameraConfigRef.current = null;
      }
      if (orbitDisabledRef.current) {
        const c = controlsRef.current;
        if (c) c.enabled = true;
        orbitDisabledRef.current = false;
      }
    }, [applyConfig]);

    /**
     * Extract orbit constraint overrides from a CameraViewpoint.
     * Returns null if no orbit overrides were specified.
     */
    const extractViewpointOverrides = useCallback(
      (vp: CameraViewpoint): Partial<CameraConfig> | null => {
        const o: Partial<CameraConfig> = {};
        let hasAny = false;
        if (vp.minPolarAngle !== undefined) {
          o.minPolarAngle = vp.minPolarAngle;
          hasAny = true;
        }
        if (vp.maxPolarAngle !== undefined) {
          o.maxPolarAngle = vp.maxPolarAngle;
          hasAny = true;
        }
        if (vp.minAzimuthAngle !== undefined) {
          o.minAzimuthAngle = vp.minAzimuthAngle;
          hasAny = true;
        }
        if (vp.maxAzimuthAngle !== undefined) {
          o.maxAzimuthAngle = vp.maxAzimuthAngle;
          hasAny = true;
        }
        if (vp.minDistance !== undefined) {
          o.minDistance = vp.minDistance;
          hasAny = true;
        }
        if (vp.maxDistance !== undefined) {
          o.maxDistance = vp.maxDistance;
          hasAny = true;
        }
        return hasAny ? o : null;
      },
      [],
    );

    // --- Schedule resume: pauses auto-rotate for resumeDelay from now ---
    const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Use a ref for the invalidate callback inside the timer so we don't
    // depend on the hook-order of useThree (which appears later in the file).
    const invalidateRef = useRef<() => void>(() => {});
    const scheduleResume = useCallback(() => {
      const cfg = getRotConfig();
      pausedUntilRef.current = performance.now() + cfg.resumeDelay;
      // frameloop="demand": schedule an invalidate() after the delay so the
      // frame loop wakes up and detects that the pause has expired.
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = setTimeout(() => {
        resumeTimerRef.current = null;
        invalidateRef.current();
      }, cfg.resumeDelay + 16); // +16ms buffer to ensure we're past the threshold
    }, [getRotConfig]);

    /**
     * Normalize azimuth to [-π, π] — call BEFORE setLookAt to prevent
     * the "unwind" effect where accumulated rotation causes a long animation.
     * Uses camera-controls v3 built-in `normalizeRotations()`.
     * @see https://github.com/yomotsu/camera-controls#normalizerotations
     */
    const normalizeAzimuth = useCallback(() => {
      controlsRef.current?.normalizeRotations();
    }, []);

    // Needed before useImperativeHandle so setAutoRotate can bootstrap the loop
    const invalidate = useThree((s) => s.invalidate);
    invalidateRef.current = invalidate; // keep ref in sync for scheduleResume timer
    const clock = useThree((s) => s.clock);

    // Expose imperative handle
    useImperativeHandle(
      ref,
      () => ({
        get controls() {
          return controlsRef.current;
        },

        reset: () => {
          const controls = controlsRef.current;
          if (!controls) {
            devWarn(
              'engine.resetCamera()',
              'CameraManager not yet ready — call after the sceneLoaded event.',
            );
            return;
          }

          // Restore any per-viewpoint constraint overrides back to base
          restoreBaseConfig();

          const t = cam.target;
          pausedUntilRef.current = Infinity;
          normalizeAzimuth();

          // frameloop="demand": flush stale clock delta so the first rendered
          // frame has a tiny delta (~16ms) instead of seconds of accumulated time.
          // Without this, camera-controls' update(hugeDelta) overshoots the
          // entire transition in one step → instant jump, no animation.
          clock.getDelta();

          transitionActiveRef.current = true;
          // Bootstrap the render loop — useFrame detects transitionActiveRef
          // and self-sustains via invalidate() until the transition completes.
          invalidate();

          controls.setLookAt(6, 2, 6, t.x, t.y, t.z, true).then(() => {
            transitionActiveRef.current = false;
            scheduleResume();
          });
        },

        setCamera: (cfg: Partial<CameraConfig>) => {
          if (!controlsRef.current) {
            devWarn(
              'engine.setCamera()',
              'CameraManager not yet ready — call after the sceneLoaded event.',
            );
            return;
          }
          applyConfig(cfg);
          // frameloop="demand": request a frame so the FOV/constraint change
          // is actually rendered (e.g. pano orientation change while idle).
          invalidate();
        },

        setViewpoint: (viewpoint: CameraViewpoint) => {
          const controls = controlsRef.current;
          if (!controls) {
            devWarn(
              'engine.setViewpoint()',
              'CameraManager not yet ready — call after the sceneLoaded event.',
            );
            return;
          }

          // Restore previous per-viewpoint overrides before applying new ones
          restoreBaseConfig();

          const { position: p, target: t } = viewpoint;
          const enableTransition = viewpoint.enableTransition ?? true;

          // Check if this viewpoint carries orbit constraint overrides
          const overrides = extractViewpointOverrides(viewpoint);
          const hasOverrides = overrides !== null || viewpoint.disableOrbit;
          const shouldRestore = viewpoint.restoreOnLeave ?? true;

          // Snapshot base config before applying overrides (only if we'll need to restore)
          if (hasOverrides && shouldRestore && !baseCameraConfigRef.current) {
            baseCameraConfigRef.current = snapshotBaseConfig();
          }

          const originalSmoothTime = controls.smoothTime;
          if (viewpoint.transitionDuration !== undefined) {
            controls.smoothTime = viewpoint.transitionDuration / 1000 / 3;
          }

          // Pause auto-rotate for entire transition
          pausedUntilRef.current = Infinity;
          normalizeAzimuth();

          // frameloop="demand": flush stale clock delta so the first rendered
          // frame has a tiny delta (~16ms) instead of seconds of accumulated time.
          // Without this, camera-controls' update(hugeDelta) overshoots the
          // entire transition in one step → instant jump, no animation.
          clock.getDelta();

          transitionActiveRef.current = true;
          // Bootstrap the render loop — useFrame detects transitionActiveRef
          // and self-sustains via invalidate() until the transition completes.
          invalidate();

          controls.setLookAt(p.x, p.y, p.z, t.x, t.y, t.z, enableTransition).then(() => {
            transitionActiveRef.current = false;
            if (controlsRef.current && viewpoint.transitionDuration !== undefined) {
              controlsRef.current.smoothTime = originalSmoothTime;
            }

            // Apply per-viewpoint orbit constraint overrides after transition
            if (overrides) {
              applyConfig(overrides);
            }
            if (viewpoint.disableOrbit && controlsRef.current) {
              controlsRef.current.enabled = false;
              orbitDisabledRef.current = true;
            }

            scheduleResume();
          });
        },

        setCameraPreset: (preset: CameraPreset) => {
          const presetConfig = CAMERA_PRESETS[preset];
          if (presetConfig) {
            applyConfig(presetConfig);
          } else {
            devWarn('engine.setCameraPreset()', `unknown preset "${preset}".`, {
              'Received preset': preset,
              'Valid presets': Object.keys(CAMERA_PRESETS),
            });
          }
        },

        setAutoRotate: (cfg: Partial<AutoRotateConfig>) => {
          autoRotateOverrideRef.current = {
            ...autoRotateOverrideRef.current,
            ...cfg,
          };
          // Update cached merged config so useFrame sees new values without allocation
          rotConfigRef.current = {
            ...DEFAULT_AUTO_ROTATE,
            ...autoRotate,
            ...autoRotateOverrideRef.current,
          };
          // frameloop="demand": bootstrap the first frame so useFrame can start
          if (rotConfigRef.current.enabled) invalidate();
        },
      }),
      [
        applyConfig,
        autoRotate,
        cam.target,
        scheduleResume,
        normalizeAzimuth,
        invalidate,
        clock,
        restoreBaseConfig,
        extractViewpointOverrides,
        snapshotBaseConfig,
      ],
    );

    // Apply camera constraints when controls are ready
    useEffect(() => {
      activeFovRef.current = cam.fov;
      activeMaxHFovRef.current = cam.maxHFov;
      applyConfig(cam);
    }, [cam, applyConfig]);

    // -----------------------------------------------------------------------
    // Landscape FOV clamp — keep hFOV ≤ MAX_HFOV_DEG so the car never looks
    // distorted on wide viewports (e.g. phone landscape, wide monitors).
    //
    // Three.js PerspectiveCamera stores vFOV. When aspect is wide, hFOV grows
    // uncontrolled. We derive the matching vFOV from MAX_HFOV and clamp.
    //
    // Uses `activeFovRef` (updated by applyConfig / presets) so the clamp
    // works correctly in pano mode where the base FOV differs from cam.fov.
    // -----------------------------------------------------------------------
    const { size } = useThree();
    useEffect(() => {
      const controls = controlsRef.current;
      if (!controls) return;

      const maxHFovDeg = activeMaxHFovRef.current;
      if (!isFinite(maxHFovDeg)) return; // clamp disabled (e.g. pano mode)

      const baseFov = activeFovRef.current; // current active FOV (prop or preset)
      const aspect = size.width / size.height;

      // hFOV that the current vFOV would produce
      const hFovRad = 2 * Math.atan(Math.tan((baseFov * Math.PI) / 360) * aspect);
      const maxHFovRad = (maxHFovDeg * Math.PI) / 180;

      let targetFov = baseFov;
      if (hFovRad > maxHFovRad) {
        // Back-calculate vFOV from the clamped hFOV
        targetFov = (2 * Math.atan(Math.tan(maxHFovRad / 2) / aspect) * 180) / Math.PI;
      }

      const innerCam = (
        controls as unknown as {
          camera: {
            isPerspectiveCamera?: boolean;
            fov: number;
            updateProjectionMatrix: () => void;
          };
        }
      ).camera;
      if (innerCam?.isPerspectiveCamera) {
        innerCam.fov = targetFov;
        innerCam.updateProjectionMatrix();
        invalidate();
      }
    }, [size.width, size.height, cam.fov, invalidate]);

    // -----------------------------------------------------------------------
    // User interaction → pause auto-rotate
    // -----------------------------------------------------------------------
    useEffect(() => {
      const controls = controlsRef.current;
      if (!controls) return;

      const onStart = () => {
        pausedUntilRef.current = Infinity; // pause indefinitely while dragging
      };
      const onEnd = () => {
        scheduleResume(); // resume after delay once user releases
      };

      controls.addEventListener('controlstart', onStart);
      controls.addEventListener('controlend', onEnd);
      return () => {
        controls.removeEventListener('controlstart', onStart);
        controls.removeEventListener('controlend', onEnd);
      };
    }, [scheduleResume]);

    // -----------------------------------------------------------------------
    // Auto-rotation render loop with ease-in
    // -----------------------------------------------------------------------

    useFrame((_, delta) => {
      const controls = controlsRef.current;
      if (!controls) return;

      const cfg = getRotConfig();
      const now = performance.now();

      // Resolve effective speed: speedPreset overrides numeric speed
      const baseSpeed = cfg.speedPreset
        ? (AUTOROTATE_SPEED_PRESETS[cfg.speedPreset] ?? cfg.speed)
        : cfg.speed;

      // Three conditions must ALL be true to rotate:
      const shouldRotate =
        cfg.enabled && // feature is on
        now >= pausedUntilRef.current; // not paused

      // Track state changes for callback & start ease-in
      if (shouldRotate !== wasRotatingRef.current) {
        wasRotatingRef.current = shouldRotate;
        onAutoRotateChange?.(shouldRotate);
        if (shouldRotate) {
          // rotation just resumed → begin ease-in
          easeStartRef.current = now;
        }
      }

      if (shouldRotate) {
        // Ease-in: smoothly ramp speed from 0 → baseSpeed
        const easeMs = cfg.easeInDuration;
        const elapsed = now - easeStartRef.current;
        // t goes 0→1 over easeInDuration; clamp at 1
        const t = easeMs > 0 ? Math.min(elapsed / easeMs, 1) : 1;
        // Cubic ease-in: slow start, natural acceleration
        const easedT = t * t * (3 - 2 * t); // smoothstep
        // invertRotation flips the sign — clockwise instead of counter-clockwise
        const direction = cfg.invertRotation ? -1 : 1;
        controls.azimuthAngle += direction * baseSpeed * easedT * delta;
        // frameloop="demand": request next frame while auto-rotating
        invalidate();
      }
      // frameloop="demand": keep rendering during viewpoint transitions
      if (transitionActiveRef.current) {
        invalidate();
      }
    });

    return <CameraControls ref={controlsRef} makeDefault />;
  },
);
